import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaSearch, FaTimes, FaDownload } from 'react-icons/fa';
import { MdFilterAlt } from 'react-icons/md';
import { AgGridTable, Pagination, Button, AdvanceFilterDrawer, getEmptyValues } from 'components/common';
import {
  setNestedPage, setNestedPageSize, setNestedParams, exportCarTransactions,
} from 'redux/booking/car/carBookingSlice';
import { debounce } from 'helpers/debounce';
import { columnDefs } from './Column';

const SEARCH_KEY = 'booking_id_or_car_name_or_driver_name_cont';

const FIELDS = [
  { key: 'booking_id', label: 'Booking ID#', type: 'text', paramKey: 'booking_id_cont', tagName: 'Booking ID#', pinned: 'left' },
  { key: 'car_name', label: 'Car name', type: 'text', paramKey: 'car_name_cont', tagName: 'Car' },
  { key: 'driver_name', label: 'Driver name', type: 'text', paramKey: 'driver_name_cont', tagName: 'Driver' },
  { key: 'confirmed_by', label: 'Confirmed by', type: 'text', paramKey: 'confirmed_by_cont', tagName: 'Confirmed by' },
  { key: 'confirmed_date', label: 'Confirmed date', type: 'date_range', paramKeys: ['confirmed_date_gteq', 'confirmed_date_lteq'], tagName: 'Confirmed date' },
  { key: 'confirmed', label: 'Confirmed', type: 'checkbox', paramKey: 'confirmed_true', tagName: 'Confirmed' },
  { key: 'closed', label: 'Closed', type: 'checkbox', paramKey: 'closed_true', tagName: 'Closed' },
];

const CarTransactionGrid = ({ stateKey, loadThunk, exportStatus }) => {
  const dispatch = useDispatch();
  const entry = useSelector((s) => s.booking_car[stateKey]) || {};
  const loading = useSelector((s) => s.booking_car.loading);
  const exporting = useSelector((s) => s.booking_car.exporting);

  const { data = [], totalRows = 0, totalPagesCount = 1, page = 1, pageSize = 50, currentParams = {} } = entry;

  const fields = useMemo(() => FIELDS, []);
  const [search, setSearch] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(FIELDS));
  const [activeCount, setActiveCount] = useState(0);

  const searchRef = useRef('');
  const advancedRef = useRef({});
  const perPageRef = useRef(pageSize);
  useEffect(() => { perPageRef.current = pageSize; }, [pageSize]);

  const runFetch = useCallback((pageNum) => {
    const params = {
      page: pageNum,
      per_page: perPageRef.current,
      ...(searchRef.current ? { [SEARCH_KEY]: searchRef.current } : {}),
      ...advancedRef.current,
    };
    dispatch(setNestedPage({ key: stateKey, page: pageNum }));
    dispatch(setNestedParams({ key: stateKey, params }));
    dispatch(loadThunk(params));
  }, [dispatch, stateKey, loadThunk]);

  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current) { didInit.current = true; dispatch(loadThunk(currentParams)); }
    // mount-only: initial load with persisted params; later loads go through runFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const debouncedSearch = useRef(debounce(() => runFetch(1), 500)).current;
  const handleSearch = (e) => { const v = e.target.value; setSearch(v); searchRef.current = v; debouncedSearch(); };
  const handleClearSearch = () => { setSearch(''); searchRef.current = ''; runFetch(1); };

  const handleApplyFilter = (params) => { advancedRef.current = params; setActiveCount(Object.keys(params).length); setShowFilter(false); runFetch(1); };
  const handleResetFilter = () => { advancedRef.current = {}; setActiveCount(0); setFilterValues(getEmptyValues(FIELDS)); runFetch(1); };
  const handleFilterChange = (key, value) => setFilterValues((prev) => ({ ...prev, [key]: value }));

  const handlePageChange = (p) => runFetch(p);
  const handlePageSizeChange = (sz) => { perPageRef.current = sz; dispatch(setNestedPageSize({ key: stateKey, pageSize: sz })); runFetch(1); };
  const handleExport = () => dispatch(exportCarTransactions({ ...currentParams, export_status: exportStatus }));

  return (
    <div>
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-mb-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-64 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input type="text" value={search} onChange={handleSearch} placeholder="Booking ID, car, driver..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-solid tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700" />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          <Button variant={activeCount > 0 ? 'info' : 'secondary'} size="sm" leftIcon={MdFilterAlt} onClick={() => setShowFilter(true)}>
            More Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </Button>
          <Button variant="secondary" size="sm" leftIcon={FaDownload} loading={exporting} onClick={handleExport}>Export</Button>

          <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">{(totalRows || 0).toLocaleString()} records</span>
        </div>
      </div>

      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
        <AgGridTable rowData={data} columnDefs={columnDefs} pagination={false} filter={false} height="60vh" loading={loading} />
        <Pagination currentPage={page} totalPages={totalPagesCount} totalResults={totalRows} perPage={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} pageSizeOptions={[50, 100, 200]} />
      </div>

      <AdvanceFilterDrawer show={showFilter} onClose={() => setShowFilter(false)} onApply={handleApplyFilter} onReset={handleResetFilter} fields={fields} values={filterValues} onChange={handleFilterChange} title="Filter car transactions" />
    </div>
  );
};

CarTransactionGrid.propTypes = {
  stateKey: PropTypes.string.isRequired,
  loadThunk: PropTypes.func.isRequired,
  exportStatus: PropTypes.string,
};

export default CarTransactionGrid;
